import { Breadcrumbs, PageHeader, useNotify } from '@hotjar/hotsauce';
import type { ProfileDetail } from '@hotjar/profile';
import { useTranslation } from '@hotjar/translations';
import { CURRENT_USER_QUERY_KEY, useCurrentUser, type User } from '@hotjar/user';
import { PROFILE } from '@hotjar/webapp/constants/routes';
import { verifyEmailCode } from '@hotjar/webapp/domains/common/verify-email/api';
import { VerificationCodeModal } from '@hotjar/webapp/features/Activation/VerificationCodeModal/VerificationCodeModal';
import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router';
import { saveProfile } from '../../domains/profile/api';
import { EmailForm } from './EmailForm';
import { Page } from './styles';

export function ProfileEmail(): JSX.Element {
  const { t } = useTranslation('profile');

  useWindowTitle(t('account-email.title'));

  const [email, setEmail] = useState('');
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const { data: currentUser } = useCurrentUser();

  const [emailCodeVerificationError, setEmailCodeVerificationError] = useState<string | null>(null);
  const { showToast } = useNotify();
  const navigate = useNavigate();

  const saveEmailChangeMutation = useMutation(
    (mutationData: { userId: number; data: ProfileDetail }) =>
      saveProfile(mutationData.userId, mutationData.data),
  );

  const verifyEmailCodeMutation = useMutation(verifyEmailCode);

  async function saveEmail(code: string) {
    if (!currentUser) {
      return;
    }

    try {
      await saveEmailChangeMutation.mutateAsync({
        userId: currentUser.id,
        data: {
          email,
          name: currentUser.name,
          role: currentUser.role,
          email_verification_code: code,
          email_verification_vid: verificationId,
        },
      });

      // Update the query cache for the current user with the new email
      queryClient.setQueryData<User>(CURRENT_USER_QUERY_KEY, () => ({
        ...currentUser,
        email,
      }));

      showToast({
        text: t('account-email.success.email-changed'),
        kind: 'success',
      });

      navigate(PROFILE.DETAILS.path);
    } catch {
      showToast({
        text: t('account-email.error.email-changed'),
        kind: 'success',
      });
    }
  }

  function onEmailVerifySuccess({
    verificationId: newVerificationId,
    email: newEmail,
  }: {
    verificationId: string;
    email: string;
  }) {
    setIsVerificationModalOpen(true);
    setEmail(newEmail);
    setVerificationId(newVerificationId);
  }

  const handleVerifyEmailCodeSubmit = async (code: string) => {
    try {
      await verifyEmailCodeMutation.mutateAsync({ code, vid: verificationId });
      await saveEmail(code);
    } catch {
      setEmailCodeVerificationError(t('account-email.error.invalid-verification-code'));
    }
  };

  const clearErrors = () => {
    setEmailCodeVerificationError(null);
  };

  return (
    <Page>
      <PageHeader
        title={t('account-email.title')}
        breadcrumbItems={[
          <Breadcrumbs.Item href={PROFILE.DETAILS.path}>
            {t('account-detail.page-title')}
          </Breadcrumbs.Item>,
          <Breadcrumbs.Item href={PROFILE.EMAIL.path}>{t('account-email.title')}</Breadcrumbs.Item>,
        ]}
      />

      <EmailForm onSuccess={onEmailVerifySuccess} />

      <VerificationCodeModal
        isOpen={isVerificationModalOpen}
        email={email}
        onSubmit={handleVerifyEmailCodeSubmit}
        onCancel={() => setIsVerificationModalOpen(false)}
        onInput={() => clearErrors}
        error={emailCodeVerificationError}
      />
    </Page>
  );
}
